import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mic, FileText, Copy, Download } from "lucide-react";

const essayIdeas = [
  { tier: "Tier 1 — Viral/Emotional", items: [
    "\"Everything Happens for a Reason\" — The Lie That Keeps You Stuck",
    "The Difference Between Boundaries and Walls (And Why You Keep Getting Them Wrong)",
    "What \"Holding Space\" Actually Means (And Why Nobody Does It Anymore)",
    "Why Your Family Hates Your Glow-Up (And What to Do About It)",
    "The Terrifying Freedom of Becoming \"That Woman\"",
  ]},
  { tier: "Tier 2 — Tactical / Pinterest Fuel", items: [
    "7 Scripts for When Someone Interrupts You Mid-Sentence",
    "10 Phrases to Shut Down a Guilt Trip Without Being Mean",
    "The \"I GET TO\" Reframe — 15 Phrases That Rewrite Your Day",
    "12 Boundaries Your Mother Never Modeled For You",
    "8 Questions to Ask Yourself Before Saying Yes",
  ]},
  { tier: "Tier 3 — Deep / Podcast-Worthy", items: [
    "The Anatomy of a Meltdown (And Why It's Not a Character Flaw)",
    "Why \"Self-Care\" Became a Lie We Sell Women",
    "The Messy Middle of Motherhood Nobody Writes Greeting Cards For",
    "You Are Not Too Much. You Are Exactly Enough.",
    "The HYGIENE Practice — Why Doing Less, Better, Changes Everything",
  ]},
  { tier: "Tier 4 — Product-Adjacent (drives sales)", items: [
    "Why I Built the Emergency Calm Kit (The 3am Story)",
    "The Personal Code of Conduct Changed My Divorce",
    "What AI Prompts Can Teach Your Kid About Feelings",
    "The 5-Minute Morning That Rebuilt My Nervous System",
    "I Tried Every Journal. Here's What Finally Worked.",
  ]},
];

const beats = [
  {
    num: "01",
    name: "The Scene",
    length: "60–90 sec",
    prompt: "Start with: \"I was ___\" or \"She was ___\". Describe ONE specific moment with sensory detail. Use her name, the weather, what she said. End with a line that lands like a punch.",
    example: "\"I was sitting across from a friend. Her mother had died three weeks earlier. She was cracked wide open...\"",
  },
  {
    num: "02",
    name: "The Name",
    length: "30–60 sec",
    prompt: "Name the invisible force at work. Coin a phrase. Plant the flag: \"This is what I call ___.\"",
    example: "\"Toxic positivity isn't kindness. It's the quiet violence of bypassing someone's truth.\"",
  },
  {
    num: "03",
    name: "The List",
    length: "3–5 min",
    prompt: "\"Here are [NUMBER] ways this shows up:\" Aim for 7–13 numbered items, each with one line of context.",
    example: "10 phrases that are toxic positivity. 13 messy middle moments every woman faces.",
  },
  {
    num: "04",
    name: "The Pattern Break",
    length: "30 sec",
    prompt: "\"Here's the part nobody tells you about...\" OR \"But here's the thing I didn't see coming...\" OR \"And then I realized something that changed everything...\"",
    example: "Your Messy Middle pivot at 1:52 — exactly this beat.",
  },
  {
    num: "05",
    name: "The Practice",
    length: "3–5 min",
    prompt: "\"Here's what I do instead. Here's the HYGIENE:\" Swaps, scripts, specifics. Short. Savable. Mention ONE product ONCE, like a friend would.",
    example: "\"Swap 'at least' for 'and also.' Swap 'you should' for 'what do you need?'\"",
  },
  {
    num: "06",
    name: "The Benediction",
    length: "30–60 sec",
    prompt: "Speak directly to the listener. Use \"you\" and \"friend\" and \"dear one.\" End with a line they'll want to screenshot.",
    example: "\"You are never alone in the middle. Welcome home. This is the work. This is the hygiene.\"",
  },
];

const checklist = [
  "Does this come from my lived experience? (Not research. Experience.)",
  "Have I named the specific — the woman, the moment, the body, the room?",
  "Have I offered a practice, not just a concept?",
  "Have I sent my reader off with a blessing, not a lecture?",
  "Would my ancestors be proud of this?",
];

export default function RecordingTemplate() {
  useEffect(() => {
    document.title = "Recording Template — Temple's Studio (Private)";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  const handlePrint = () => window.print();

  return (
    <div className="bg-[#efeae7] min-h-screen" data-testid="recording-template">
      <header className="fixed top-0 w-full z-50 bg-white/90 backdrop-blur-xl border-b border-[#CCFBF1] print:hidden">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-[#25a1c2] hover:text-[#042F2E] text-sm font-medium inline-flex items-center gap-1.5">
            <ArrowLeft className="w-4 h-4" /> Home
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-[10px] font-bold uppercase tracking-widest text-[#25a1c2] bg-[#F0F5F4] px-2.5 py-1 rounded-full">
              Private · Not Indexed
            </span>
            <button
              onClick={handlePrint}
              className="inline-flex items-center gap-1.5 bg-[#042F2E] hover:bg-[#068fa2] text-white text-xs font-bold rounded-md px-3 py-1.5"
              data-testid="print-button"
            >
              <Download className="w-3.5 h-3.5" /> Print / PDF
            </button>
          </div>
        </div>
      </header>

      <section className="pt-28 pb-10 px-6 print:pt-8">
        <div className="max-w-5xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-[#FEF3C7] text-[#92400E] px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-widest mb-4">
            <Mic className="w-3.5 h-3.5" /> Temple's Studio
          </div>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tighter text-[#042F2E] mb-4" data-testid="template-title">
            The 6-Beat Essay Template
          </h1>
          <p className="font-sans text-[#068fa2] text-lg max-w-2xl leading-relaxed">
            The Temple Franklin Essay Format (TFEF). Every essay, same 6 beats.
            Fill in the blanks, read aloud once, record. Target runtime: 10–12 min.
          </p>
        </div>
      </section>

      <section className="pb-12 px-6">
        <div className="max-w-5xl mx-auto space-y-5">
          {beats.map((b) => (
            <div
              key={b.num}
              className="bg-white rounded-2xl border border-[#CCFBF1] p-6 md:p-8"
              data-testid={`beat-${b.num}`}
            >
              <div className="flex flex-col md:flex-row md:items-baseline gap-2 md:gap-4 mb-3">
                <span className="font-serif text-5xl text-[#5EEAD4]">{b.num}</span>
                <div className="flex-1">
                  <h2 className="font-serif text-2xl md:text-3xl text-[#042F2E]">
                    {b.name}
                  </h2>
                  <p className="text-[#25a1c2] text-xs font-bold uppercase tracking-widest mt-0.5">
                    {b.length}
                  </p>
                </div>
              </div>
              <p className="text-[#068fa2] leading-relaxed mb-3">{b.prompt}</p>
              <div className="bg-[#F0FDFA] border-l-4 border-[#5EEAD4] rounded-r-lg px-4 py-3">
                <p className="text-[11px] font-bold uppercase tracking-widest text-[#25a1c2] mb-1">
                  Example from your own work
                </p>
                <p className="text-[#068fa2] italic">{b.example}</p>
              </div>
              <div className="mt-4 p-4 bg-[#FAFAF9] border border-dashed border-[#A7A29E] rounded-lg min-h-[80px] print:min-h-[140px]">
                <p className="text-[11px] font-bold uppercase tracking-widest text-[#78716C]">
                  Write your {b.name.toLowerCase()} here
                </p>
              </div>
            </div>
          ))}

          <div className="bg-[#042F2E] rounded-2xl p-6 md:p-8 text-white">
            <p className="text-[#5EEAD4] text-xs font-bold uppercase tracking-widest mb-2">
              CTA For Show Notes
            </p>
            <p className="font-serif text-xl mb-3">Every essay closes with:</p>
            <ul className="space-y-2 text-white/90 text-sm leading-relaxed">
              <li>→ Get the [PRODUCT] at mindbodyspirithygienetools.com/[path]</li>
              <li>→ Start the free 52-week journey at mindbodyspirithygienetools.com</li>
              <li>→ Full essay + audio player at mindbodyspirithygienetools.com/blog/[slug]</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="pb-12 px-6 print:break-before-page">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-serif text-3xl md:text-4xl text-[#042F2E] mb-2">
            Your Ancestors' Litmus Test
          </h2>
          <p className="text-[#068fa2] mb-6 max-w-2xl">
            Before you hit publish on any essay, ask yourself:
          </p>
          <div className="bg-white rounded-2xl border border-[#CCFBF1] p-6 md:p-8 space-y-3">
            {checklist.map((q, i) => (
              <div key={i} className="flex items-start gap-3" data-testid={`checklist-${i}`}>
                <div className="w-6 h-6 rounded-md border-2 border-[#5EEAD4] flex-shrink-0 mt-0.5" />
                <p className="text-[#068fa2] leading-relaxed">{q}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-[#068fa2] italic mt-6">
            All five = yes, ship it.<br />
            Any no = return to the mic.
          </p>
        </div>
      </section>

      <section className="pb-24 px-6 print:break-before-page">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-serif text-3xl md:text-4xl text-[#042F2E] mb-2">
            Your 20 Ready-to-Record Essay Ideas
          </h2>
          <p className="text-[#068fa2] mb-8 max-w-2xl">
            Mined from your own lived experience, existing content, and HYGIENE system.
            Pick the one that makes your chest tighten — that's the one to record next.
          </p>
          <div className="grid md:grid-cols-2 gap-5">
            {essayIdeas.map((tier) => (
              <div
                key={tier.tier}
                className="bg-white rounded-2xl border border-[#CCFBF1] p-6"
                data-testid={`tier-${tier.tier.slice(0, 6)}`}
              >
                <p className="text-[#25a1c2] text-xs font-bold uppercase tracking-widest mb-3">
                  {tier.tier}
                </p>
                <ol className="space-y-2.5 text-[#068fa2] leading-relaxed text-sm list-decimal pl-5">
                  {tier.items.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ol>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="py-10 bg-[#042F2E] print:hidden">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <p className="font-sans text-[#5EEAD4] text-xs">
            Private studio page for Temple. Not indexed. © 2026 Mind Body Spirit HYGIENE™ Tools.
          </p>
        </div>
      </footer>
    </div>
  );
}
